import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import moment from 'moment';
import { FileListTable } from './FileListTable';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  fileIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  fileTableContainer: {
    maxHeight: 400,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    width: '100%',
  },
  fullWidth: {
    width: '100%',
  },
}));

const SelectCustomData = ({
  files = [],
  isLoading = false,
  isError = false,
  customDataId = null,
  onSelect = () => {},
  errorMessage = 'An error occurred while loading files.',
}) => {
  const classes = useStyles();
  const [selectedFileId, setSelectedFileId] = useState(customDataId);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Update selected file ID when customDataId prop changes
  useEffect(() => {
    setSelectedFileId(customDataId);
  }, [customDataId]);

  const selectedFile = files?.find((file) => file.user_file_id === selectedFileId);

  const handleFileSelect = (fileId) => {
    setSelectedFileId(fileId);
    onSelect(fileId);
    setDialogOpen(false);
  };

  const openFileSelector = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" variant="outlined" className={classes.fullWidth}>
        {errorMessage}
      </Alert>
    );
  }

  return (
    <div className={classes.root}>
      {selectedFileId && selectedFile ? (
        <Card variant="outlined" className={classes.fileCard}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={1} justifyContent="center">
              <InsertDriveFileIcon className={classes.fileIcon} />
              <Typography variant="h6">{selectedFile.file_name}</Typography>
            </Box>
            <Grid container spacing={2} justify="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary" align="center">
                  Uploaded: {moment(selectedFile.created_at).format('MM-DD-YYYY HH:mm')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary" align="center">
                  Last updated: {moment(selectedFile.updated_at).format('MM-DD-YYYY HH:mm')}
                </Typography>
              </Grid>
            </Grid>

            <Box mt={2} display="flex" justifyContent="center">
              <Button variant="outlined" color="primary" onClick={openFileSelector}>
                Change File
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box mb={2} className={classes.fullWidth} display="flex" justifyContent="center">
          <FormControl error={!selectedFileId}>
            <Button
              variant="contained"
              color="primary"
              onClick={openFileSelector}
              disabled={files.length === 0}
            >
              Select a File
            </Button>
            {files.length === 0 && <FormHelperText>No files available to select</FormHelperText>}
          </FormControl>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>Select a File</DialogTitle>
        <DialogContent>
          {files.length === 0 ? (
            <Typography variant="body1" align="center">
              No files available
            </Typography>
          ) : (
            <FileListTable
              files={files}
              isLoading={false}
              selectable={true}
              selectedFileId={selectedFileId}
              onFileSelect={handleFileSelect}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SelectCustomData;
